import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { messagesApi } from '@/api/endpoints'
import { errorMessage } from '@/api/client'
import {
  Empty,
  ErrorNote,
  Loading,
  PageHeader,
  Pagination,
  Toolbar,
} from '@/components/common/Ui'
import { dateTime, titleCase } from '@/lib/format'
import { PERMS, can, useAuth } from '@/store/auth'

const PAGE_SIZE = 25
const STATUSES = ['received', 'transcribing', 'extracting', 'matching', 'completed', 'failed']

const TYPE_ICONS = { text: '💬', audio: '🎤', image: '📷', document: '📄', interactive: '🔘' }

/**
 * Raw inbound messages — mainly a troubleshooting view.
 *
 * The common case here is a message from a number that isn't registered yet:
 * add the customer, then reprocess, and the order is created without asking
 * the customer to send it again.
 */
export default function Messages() {
  const { user } = useAuth()
  const canReprocess = can(user, PERMS.messagesReprocess)
  const queryClient = useQueryClient()
  const [status, setStatus] = useState('')
  const [unlinked, setUnlinked] = useState(false)
  const [page, setPage] = useState(1)
  const [error, setError] = useState('')

  const params = { page, page_size: PAGE_SIZE }
  if (status) params.status = status
  if (unlinked) params.unlinked = true

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['messages', params],
    queryFn: () => messagesApi.list(params),
  })

  const reprocess = useMutation({
    mutationFn: (id) => messagesApi.reprocess(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['messages'] })
      setError('')
    },
    onError: (e) => setError(errorMessage(e)),
  })

  return (
    <div className="messages-page">
      <PageHeader title="Messages" subtitle="Everything customers have sent in" />

      <Toolbar>
        <select
          value={status}
          onChange={(e) => {
            setStatus(e.target.value)
            setPage(1)
          }}
        >
          <option value="">All statuses</option>
          {STATUSES.map((s) => (
            <option key={s} value={s}>
              {titleCase(s)}
            </option>
          ))}
        </select>
        <label className="checkbox">
          <input
            type="checkbox"
            checked={unlinked}
            onChange={(e) => {
              setUnlinked(e.target.checked)
              setPage(1)
            }}
          />
          <span>Unknown senders only</span>
        </label>
      </Toolbar>

      {error && <ErrorNote message={error} />}
      {isLoading && <Loading />}
      {isError && <ErrorNote message="Could not load messages" onRetry={refetch} />}
      {data && !data.items.length && <Empty icon="💬" title="No messages" />}

      {data && data.items.length > 0 && (
        <>
          <table className="table">
            <thead>
              <tr>
                <th>Type</th>
                <th>From</th>
                <th>Content</th>
                <th>Status</th>
                <th>Received</th>
                {canReprocess && <th />}
              </tr>
            </thead>
            <tbody>
              {data.items.map((m) => (
                <tr key={m.id} className={m.status === 'failed' ? 'row-danger' : ''}>
                  <td>{TYPE_ICONS[m.msg_type] ?? '•'}</td>
                  <td className="mono">
                    {m.from_phone}
                    {!m.customer_id && <span className="badge warn-sm">unknown</span>}
                  </td>
                  <td className="truncate">
                    {m.transcript || m.ocr_text || m.body || <span className="muted">—</span>}
                    {m.error && <div className="error-inline">{m.error}</div>}
                  </td>
                  <td>
                    <span className={`badge proc-${m.status}`}>{titleCase(m.status)}</span>
                  </td>
                  <td className="muted">{dateTime(m.received_at)}</td>
                  {canReprocess && (
                    <td className="right">
                      {m.status === 'failed' && (
                        <button
                          type="button"
                          className="btn btn-sm"
                          disabled={reprocess.isPending}
                          onClick={() => reprocess.mutate(m.id)}
                        >
                          Retry
                        </button>
                      )}
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>

          <Pagination
            page={data.page}
            pageSize={data.page_size}
            total={data.total}
            onChange={setPage}
          />
        </>
      )}
    </div>
  )
}
